import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import {
    Building2,
    Users,
    UsersRound,
    AppWindow,
    ShieldCheck,
    Key,
    BarChart3,
    ChevronLeft,
    ChevronDown,
    Moon,
    Sun,
    X,
    UserCog,
    Layers,
    CheckSquare,
    Activity,
    Bot,
    Sliders,
    Settings2,
    Mail,
    KeyRound,
} from 'lucide-react';

import { cn } from '@/utils';
import { useTheme } from '@/contexts/theme-context';
import { useLayout } from '@/contexts/layout-context';

interface NavItemProps {
    icon: React.ElementType;
    label: string;
    to: string;
    beta?: boolean;
    children?: React.ReactNode;
}

const NavItem = ({ icon: Icon, label, to, beta, children }: NavItemProps) => (
    <div className="space-y-1">
        <NavLink
            to={to}
            className={({ isActive }) => cn(
                "flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                isActive
                    ? "bg-(--primary-light) text-(--primary) dark:bg-(--primary)/10 dark:text-(--primary)"
                    : "text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-900"
            )}
        >
            {({ isActive }) => (
                <>
                    <Icon className={cn("w-4 h-4 mr-3 transition-colors", isActive ? "text-(--primary)" : "text-slate-400")} />
                    <span className="flex-1">{label}</span>
                    {beta && (
                        <span className="ml-auto text-[10px] bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 px-1.5 rounded uppercase font-bold tracking-wider">
                            Beta
                        </span>
                    )}
                </>
            )}
        </NavLink>
        {children && <div className="ml-9 space-y-1">{children}</div>}
    </div>
);

// ─── Collapsible Parent Section ───────────────────────────────────────────────

interface CollapsibleSectionProps {
    title: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
}

const CollapsibleSection = ({ title, defaultOpen = true, children }: CollapsibleSectionProps) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="space-y-1">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest hover:text-slate-600 dark:hover:text-slate-300 transition-colors group cursor-pointer"
            >
                <span>{title}</span>
                <ChevronDown className={cn("w-3.5 h-3.5 transition-transform duration-200 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300", isOpen ? "" : "-rotate-90")} />
            </button>
            {isOpen && <div className="space-y-1 animate-in fade-in duration-200">{children}</div>}
        </div>
    );
};

// ─── Expandable Nav Item (Parent with nested sub-items) ──────────────────────

interface ExpandableNavItemProps {
    icon: React.ElementType;
    label: string;
    to: string;
    children: React.ReactNode;
}

const ExpandableNavItem = ({ icon: Icon, label, to, children }: ExpandableNavItemProps) => {
    const location = useLocation();
    const isChildActive = location.pathname.startsWith(to);
    const [isOpen, setIsOpen] = useState(isChildActive);

    useEffect(() => {
        if (isChildActive) setIsOpen(true);
    }, [isChildActive]);

    return (
        <div className="space-y-1">
            <div className="flex items-center">
                <NavLink
                    to={to}
                    className={({ isActive }) => cn(
                        "flex-1 flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                        isActive || isChildActive
                            ? "bg-(--primary-light) text-(--primary) dark:bg-(--primary)/10 dark:text-(--primary)"
                            : "text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-900"
                    )}
                >
                    {({ isActive }) => (
                        <>
                            <Icon className={cn("w-4 h-4 mr-3 transition-colors", isActive || isChildActive ? "text-(--primary)" : "text-slate-400")} />
                            <span className="flex-1">{label}</span>
                        </>
                    )}
                </NavLink>
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                    title={isOpen ? "Collapse" : "Expand"}
                >
                    <ChevronDown className={cn("w-3.5 h-3.5 transition-transform duration-200", isOpen ? "" : "-rotate-90")} />
                </button>
            </div>
            {isOpen && (
                <div className="ml-5 border-l border-slate-200 dark:border-slate-800 pl-3 space-y-1 animate-in fade-in duration-200">
                    {children}
                </div>
            )}
        </div>
    );
};

// ─── Main Sidebar Component ───────────────────────────────────────────────────

export const Sidebar = () => {
    const { theme, toggleTheme } = useTheme();
    const { isSidebarOpen, closeSidebar } = useLayout();
    const isDark = theme === 'dark';

    return (
        <>
            {/* Mobile Overlay */}
            <div
                className={cn(
                    "fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300",
                    isSidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                )}
                onClick={closeSidebar}
            />

            <aside
                className={cn(
                    "fixed inset-y-0 left-0 z-50 w-(--sidebar-width) bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 flex flex-col h-full transition-all duration-300 lg:static lg:translate-x-0",
                    isSidebarOpen ? "translate-x-0" : "-translate-x-full"
                )}
            >
                {/* Logo */}
                <div className="p-5 flex items-center justify-between border-b border-slate-100 dark:border-slate-900">
                    <Link to="/m" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
                        <img src="/brand/light-logo-with-text.svg" alt="Argus" className="h-7 w-auto dark:hidden" />
                        <img src="/brand/dark-logo-with-text.svg" alt="Argus" className="h-7 w-auto hidden dark:block" />
                        <span className="font-semibold text-xs tracking-wider uppercase px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">IAM</span>
                    </Link>
                    <button
                        type='button'
                        onClick={closeSidebar}
                        className="lg:hidden text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <button
                        type='button'
                        className="hidden lg:block text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                </div>

                {/* Navigation with Collapsible Sections */}
                <nav className="flex-1 overflow-y-auto p-4 space-y-5 sidebar-scrollbar">
                    {/* SECTION 1: CORE MODULES */}
                    <CollapsibleSection title="Core Modules" defaultOpen={true}>
                        <NavItem icon={Building2} label="Tenants" to="/m/tenants" />
                        <NavItem icon={Users} label="Users" to="/m/users" />
                        <NavItem icon={UsersRound} label="Groups" to="/m/groups" />
                        <NavItem icon={AppWindow} label="Applications" to="/m/applications" />
                        <NavItem icon={Bot} label="Service Principals" to="/m/service-principals" />
                        <NavItem icon={Layers} label="Resources" to="/m/resources" />
                        <NavItem icon={Key} label="Identity Providers" to="/m/idps" />
                    </CollapsibleSection>

                    {/* SECTION 2: SERVICES */}
                    <CollapsibleSection title="Services" defaultOpen={true}>
                        <NavItem icon={Mail} label="Email" to="/m/mail" />
                    </CollapsibleSection>

                    {/* SECTION 3: TASKS */}
                    <CollapsibleSection title="Tasks" defaultOpen={true}>
                        <NavItem icon={CheckSquare} label="Tasks" to="/m/tasks" />
                    </CollapsibleSection>

                    {/* SECTION 4: SECURITY & COMPLIANCE */}
                    <CollapsibleSection title="Security & Compliance" defaultOpen={true}>
                        <NavItem icon={ShieldCheck} label="Roles" to="/m/roles" />
                        <NavItem icon={Key} label="Permissions" to="/m/permissions" />
                        <NavItem icon={UserCog} label="Assignments" to="/m/assignments" />
                        <NavItem icon={Activity} label="Activities" to="/m/activities" />
                        <NavItem icon={BarChart3} label="Analytics" to="/m/analytics" />
                        <NavItem icon={KeyRound} label="Token Management" to="/m/tokens" />
                    </CollapsibleSection>

                    {/* SECTION 5: SYSTEM SETTINGS & CONFIGURATIONS */}
                    <CollapsibleSection title="System Settings" defaultOpen={true}>
                        <NavItem icon={Sliders} label="Events" to="/m/events" />

                        {/* Expandable Configurations with nested sub-items */}
                        <ExpandableNavItem icon={Settings2} label="Configurations" to="/m/configurations">
                            <NavItem icon={Mail} label="Email Providers" to="/m/configurations/email" />
                        </ExpandableNavItem>
                    </CollapsibleSection>
                </nav>

                {/* Footer */}
                <div className="p-4 border-t border-slate-100 dark:border-slate-900 bg-slate-50/50 dark:bg-slate-900/20">
                    <div className="flex items-center justify-between px-2">
                        <div className="flex items-center text-sm font-medium text-slate-600 dark:text-slate-400">
                            {isDark ? <Moon className="w-4 h-4 mr-3" /> : <Sun className="w-4 h-4 mr-3" />}
                            Dark mode
                        </div>
                        <button
                            onClick={toggleTheme}
                            className={cn(
                                "w-10 h-5 rounded-full relative transition-colors duration-200",
                                isDark ? "bg-(--primary)" : "bg-slate-200 dark:bg-slate-800"
                            )}
                        >
                            <div className={cn(
                                "absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200",
                                isDark ? "translate-x-5" : "translate-x-0.5"
                            )} />
                        </button>
                    </div>
                </div>
            </aside>
        </>
    );
};
